import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const COLORS = {
  gold: '#f0c040',
  cyan: '#00d4ff',
  green: '#00e87a',
  red: '#ff4444',
  bg: '#04060f',
  cardBg: '#0a1020',
  muted: '#4a5a7a',
};

// Auto-generate a slug from a set name.
// Rules based on existing CollectionKeeper slugs: drop "Topps", drop "Star Wars",
// lowercase, collapse whitespace to single dashes, strip most punctuation.
function generateSlug(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/\btopps\b/g, '')
    .replace(/\bstar wars\b/g, '')
    .replace(/[^a-z0-9\s-]/g, '')  // strip punctuation
    .replace(/\s+/g, '-')          // spaces to dashes
    .replace(/-+/g, '-')           // collapse multiple dashes
    .replace(/^-|-$/g, '');        // trim leading/trailing dashes
}

export default function AdminChecklists({ user }) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sets, setSets] = useState([]);
  const [error, setError] = useState(null);

  // New-set form state
  const [showNewSetForm, setShowNewSetForm] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [newSetYear, setNewSetYear] = useState('');
  const [newSetManufacturer, setNewSetManufacturer] = useState('Topps');
  const [newSetSlug, setNewSetSlug] = useState('');
  const [newSetSlugManuallyEdited, setNewSetSlugManuallyEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function init() {
      setLoading(true);
      try {
        const { data: adminRow, error: roleErr } = await supabase
          .from('admin_users')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (roleErr) throw roleErr;
        if (!mounted) return;

        if (!adminRow) {
          setRole(null);
          setLoading(false);
          return;
        }
        setRole(adminRow.role);
        await loadSets(mounted);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Failed to load admin page');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    init();
    return () => { mounted = false; };
  }, [user.id]);

  async function loadSets(mounted = true) {
    const { data: setsData, error: setsErr } = await supabase
      .from('sets')
      .select('id, slug, name, year, manufacturer, is_published, is_archived, updated_at')
      .eq('is_archived', false)
      .order('year', { ascending: false })
      .order('name', { ascending: true });

    if (setsErr) throw setsErr;
    if (mounted) setSets(setsData || []);
  }

  const goHome = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  // ---------------------------------------------------------------------------
  // New-set form handlers
  // ---------------------------------------------------------------------------

  const openNewSetForm = () => {
    setNewSetName('');
    setNewSetYear('');
    setNewSetManufacturer('Topps');
    setNewSetSlug('');
    setNewSetSlugManuallyEdited(false);
    setFormError(null);
    setShowNewSetForm(true);
  };

  const cancelNewSetForm = () => {
    setShowNewSetForm(false);
    setFormError(null);
  };

  const onNameChange = (e) => {
    const value = e.target.value;
    setNewSetName(value);
    if (!newSetSlugManuallyEdited) {
      setNewSetSlug(generateSlug(value));
    }
  };

  const onSlugChange = (e) => {
    setNewSetSlug(e.target.value);
    setNewSetSlugManuallyEdited(true);
  };

  const saveNewSet = async () => {
    setFormError(null);

    // Validate
    if (!newSetName.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!newSetYear || isNaN(parseInt(newSetYear))) {
      setFormError('Year must be a valid number.');
      return;
    }
    if (!newSetSlug.trim()) {
      setFormError('Slug is required.');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(newSetSlug)) {
      setFormError('Slug can only contain lowercase letters, numbers, and dashes.');
      return;
    }

    setSaving(true);
    try {
      const { error: insertErr } = await supabase
        .from('sets')
        .insert({
          slug: newSetSlug.trim(),
          name: newSetName.trim(),
          year: parseInt(newSetYear),
          manufacturer: newSetManufacturer.trim() || 'Topps',
          is_published: false,
          created_by: user.id,
        });

      if (insertErr) {
        if (insertErr.code === '23505' || (insertErr.message && insertErr.message.includes('unique'))) {
          setFormError(`A set with slug "${newSetSlug}" already exists. Pick a different slug.`);
        } else {
          setFormError(insertErr.message || 'Save failed.');
        }
        setSaving(false);
        return;
      }

      await loadSets();
      setShowNewSetForm(false);
    } catch (err) {
      setFormError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ color: COLORS.muted, fontFamily: 'Share Tech Mono, monospace', fontSize: 13 }}>
          Loading admin...
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={headerTitle}>ADMIN · CHECKLISTS</div>
          <div style={{ color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 16, marginTop: 24 }}>
            You don't have admin access.
          </div>
          <div style={{ color: COLORS.muted, fontFamily: 'Rajdhani, sans-serif', fontSize: 14, marginTop: 8 }}>
            If you believe this is a mistake, contact the owner.
          </div>
          <button onClick={goHome} style={{ ...buttonOutlineStyle, marginTop: 20 }}>BACK TO APP</button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={headerTitle}>ADMIN · CHECKLISTS</div>
          <div style={{ color: COLORS.red, fontFamily: 'Share Tech Mono, monospace', fontSize: 13, marginTop: 24, whiteSpace: 'pre-wrap' }}>
            Error: {error}
          </div>
          <button onClick={goHome} style={{ ...buttonOutlineStyle, marginTop: 20 }}>BACK TO APP</button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={headerTitle}>ADMIN · CHECKLISTS</div>
          <div style={{
            fontFamily: 'Share Tech Mono, monospace',
            color: COLORS.green,
            fontSize: 11,
            letterSpacing: 2,
          }}>
            {(typeof window !== 'undefined' && window.location.hostname === 'collectionkeeper.org') ? 'PRODUCTION' : 'STAGING'} · {role.toUpperCase()}
          </div>
        </div>

        <div style={{
          fontFamily: 'Rajdhani, sans-serif',
          color: COLORS.muted,
          fontSize: 14,
          marginBottom: 24,
        }}>
          Manage sets, sub-sets, cards, parallels, and scanner hints.
        </div>

        {/* Top actions */}
        {!showNewSetForm && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <button
              onClick={openNewSetForm}
              style={buttonPrimaryStyle}
              disabled={role !== 'owner'}
            >
              + NEW SET
            </button>
            <button onClick={goHome} style={buttonOutlineStyle}>BACK TO APP</button>
            <div style={{ flex: 1 }} />
            <button onClick={signOut} style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}>
              SIGN OUT
            </button>
          </div>
        )}

        {/* Inline new-set form */}
        {showNewSetForm && (
          <div style={{
            background: COLORS.cardBg,
            border: `1px solid ${COLORS.cyan}`,
            borderRadius: 6,
            padding: 16,
            marginBottom: 20,
          }}>
            <div style={{
              fontFamily: 'Orbitron, sans-serif',
              color: COLORS.cyan,
              fontSize: 12,
              letterSpacing: 1.5,
              marginBottom: 14,
            }}>
              NEW SET
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={formLabelStyle}>NAME</label>
                <input
                  type="text"
                  value={newSetName}
                  onChange={onNameChange}
                  style={formInputStyle}
                  placeholder="e.g. 2026 Topps Chrome Star Wars Galaxy"
                  autoFocus
                />
              </div>
              <div>
                <label style={formLabelStyle}>YEAR</label>
                <input
                  type="number"
                  value={newSetYear}
                  onChange={(e) => setNewSetYear(e.target.value)}
                  style={formInputStyle}
                  placeholder="2026"
                />
              </div>
              <div>
                <label style={formLabelStyle}>MANUFACTURER</label>
                <input
                  type="text"
                  value={newSetManufacturer}
                  onChange={(e) => setNewSetManufacturer(e.target.value)}
                  style={formInputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={formLabelStyle}>SLUG</label>
              <input
                type="text"
                value={newSetSlug}
                onChange={onSlugChange}
                style={formInputStyle}
                placeholder="e.g. 2026-chrome-galaxy"
              />
              <div style={{
                fontFamily: 'Share Tech Mono, monospace',
                color: COLORS.muted,
                fontSize: 11,
                marginTop: 4,
              }}>
                {newSetSlugManuallyEdited
                  ? 'Manually edited. Must be unique across all sets.'
                  : 'Auto-generated from name. Edit if needed. Must be unique across all sets.'}
              </div>
            </div>

            {formError && (
              <div style={{
                color: COLORS.red,
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: 13,
                marginBottom: 10,
                padding: 8,
                background: `${COLORS.red}15`,
                borderRadius: 4,
              }}>
                {formError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button
                onClick={saveNewSet}
                disabled={saving}
                style={{ ...buttonPrimaryStyle, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'SAVING...' : 'SAVE DRAFT'}
              </button>
              <button
                onClick={cancelNewSetForm}
                disabled={saving}
                style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}
              >
                CANCEL
              </button>
            </div>
          </div>
        )}

        {/* Sets list */}
        <div style={{
          background: COLORS.cardBg,
          borderRadius: 8,
          overflow: 'hidden',
          border: `1px solid ${COLORS.muted}22`,
        }}>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 80px 120px 140px 80px',
            padding: '10px 16px',
            background: COLORS.bg,
            borderBottom: `1px solid ${COLORS.muted}22`,
            fontFamily: 'Orbitron, sans-serif',
            fontSize: 11,
            color: COLORS.gold,
            letterSpacing: 1,
          }}>
            <div>SET</div>
            <div>YEAR</div>
            <div>MANUFACTURER</div>
            <div>STATUS</div>
            <div>ACTIONS</div>
          </div>

          {sets.length === 0 ? (
            <div style={{
              padding: '40px 16px',
              textAlign: 'center',
              fontFamily: 'Rajdhani, sans-serif',
              color: COLORS.muted,
              fontSize: 14,
            }}>
              No sets yet. Click <span style={{ color: COLORS.cyan }}>+ NEW SET</span> to create one.
            </div>
          ) : (
            sets.map(s => (
              <div key={s.id} style={{
                display: 'grid',
                gridTemplateColumns: '2fr 80px 120px 140px 80px',
                padding: '12px 16px',
                borderBottom: `1px solid ${COLORS.muted}22`,
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: 14,
                color: '#fff',
                alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: COLORS.muted, fontFamily: 'Share Tech Mono, monospace' }}>{s.slug}</div>
                </div>
                <div>{s.year}</div>
                <div>{s.manufacturer}</div>
                <div>
                  {s.is_published ? (
                    <span style={{ color: COLORS.green, fontSize: 12, fontFamily: 'Share Tech Mono, monospace' }}>PUBLISHED</span>
                  ) : (
                    <span style={{ color: COLORS.gold, fontSize: 12, fontFamily: 'Share Tech Mono, monospace' }}>DRAFT</span>
                  )}
                </div>
                <div>
                  <button
                    onClick={() => {
                      window.history.pushState({}, '', `/admin/checklists/edit/${s.slug}`);
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                    style={{ ...buttonOutlineStyle, padding: '4px 10px', fontSize: 11 }}
                  >
                    EDIT
                  </button>
                </div>
              </div>
            ))
          )}

        </div>

        {/* Footer hints */}
        <div style={{
          marginTop: 24,
          padding: 12,
          background: COLORS.cardBg,
          borderRadius: 4,
          fontFamily: 'Share Tech Mono, monospace',
          color: COLORS.muted,
          fontSize: 11,
          lineHeight: 1.6,
        }}>
          Phase 1c: new sets can be created as drafts. Sub-sets, cards, parallels, and publish flows come next.
          {role === 'super_user' && <div style={{ marginTop: 6 }}>Super users can edit only sub-sets they've been granted access to.</div>}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shared styles
// -----------------------------------------------------------------------------

const containerStyle = {
  minHeight: '100vh',
  background: COLORS.bg,
  padding: '24px 16px',
  fontFamily: 'Rajdhani, sans-serif',
  color: '#fff',
};

const headerTitle = {
  fontFamily: 'Orbitron, sans-serif',
  color: COLORS.gold,
  fontSize: 20,
  letterSpacing: 1,
};

const buttonPrimaryStyle = {
  padding: '10px 18px',
  background: COLORS.gold,
  color: COLORS.bg,
  border: 'none',
  borderRadius: 4,
  fontFamily: 'Orbitron, sans-serif',
  fontSize: 12,
  letterSpacing: 1.5,
  fontWeight: 700,
  cursor: 'pointer',
};

const buttonOutlineStyle = {
  padding: '10px 18px',
  background: 'transparent',
  color: COLORS.cyan,
  border: `1px solid ${COLORS.cyan}`,
  borderRadius: 4,
  fontFamily: 'Orbitron, sans-serif',
  fontSize: 12,
  letterSpacing: 1.5,
  cursor: 'pointer',
};

const formLabelStyle = {
  fontFamily: 'Orbitron, sans-serif',
  fontSize: 10,
  color: COLORS.gold,
  letterSpacing: 1,
  marginBottom: 4,
  display: 'block',
};

const formInputStyle = {
  width: '100%',
  background: COLORS.bg,
  color: '#fff',
  border: `1px solid ${COLORS.muted}66`,
  borderRadius: 4,
  padding: '8px 10px',
  fontFamily: 'Share Tech Mono, monospace',
  fontSize: 13,
  boxSizing: 'border-box',
};
